import { useState } from "react";
import { Box, Paper, Typography, Collapse, Divider } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { METRICS, METRIC_EXPLANATIONS } from "./constants";

const MetricsGuide = () => {
  const [open, setOpen] = useState(false);

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Box
        component="button"
        type="button"
        aria-expanded={open}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
          userSelect: "none",
          width: "100%",
          p: 0,
          border: 0,
          background: "none",
          font: "inherit",
          color: "inherit",
          textAlign: "left",
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <InfoOutlinedIcon color="info" />
        <Typography
          sx={{
            fontWeight: 600,
            flexGrow: 1,
          }}
        >
          Guide de lecture — Algorithme & métriques
        </Typography>
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
              }}
            >
              L'algorithme utilisé —{" "}
              <span style={{ fontWeight: 400 }}>
                Random Forest (Forêt aléatoire)
              </span>
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                mt: 0.5,
              }}
            >
              Imaginez 100 conseillers qui regardent chacun un sous-ensemble de
              vos données historiques et votent : «cette réservation va-t-elle
              être annulée ?». Le modèle prend le vote majoritaire. Cette
              approche (appelée <em>bagging</em>) rend les prédictions beaucoup
              plus stables qu'un seul conseiller.
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                mt: 0.5,
              }}
            >
              <strong>Pourquoi c'est adapté à votre contexte RM :</strong> Les
              annulations sont rares (données déséquilibrées), les variables
              sont de natures très différentes (dates de réservation, codes
              tarifaires, canaux, pays d'origine...), et le modèle n'a pas
              besoin d'être ré-entraîné à chaque nouvelle réservation — il
              apprend sur l'historique et prédit en temps réel.
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                mt: 0.5,
              }}
            >
              <strong>Paramètre clé :</strong> 100 arbres de décision, entraînés
              sur vos données passées avec une séparation temporelle (les
              données les plus récentes servent à valider, les plus anciennes à
              apprendre).
            </Typography>
          </Box>
          <Divider />
          {METRICS.map((key) => {
            const info = METRIC_EXPLANATIONS[key]!;
            return (
              <Box key={key}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                  }}
                >
                  {info.label} —{" "}
                  <span style={{ fontWeight: 400 }}>{info.simple}</span>
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  {info.detail}
                </Typography>
              </Box>
            );
          })}
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
              }}
            >
              La matrice de confusion —{" "}
              <span style={{ fontWeight: 400 }}>
                lire les erreurs du modèle
              </span>
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                mt: 0.5,
              }}
            >
              La matrice de confusion montre comment le modèle se trompe sur le
              jeu de test (les 20% de réservations les plus récentes). Elle
              distingue 4 cas :
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                mt: 0.5,
              }}
            >
              <strong>Vrai Négatif (TN) :</strong> réservation confirmée, modèle
              a bien prédit "confirmée". <strong>Vrai Positif (TP) :</strong>{" "}
              réservation annulée, modèle a bien prédit "annulée".
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                mt: 0.5,
              }}
            >
              <strong>Faux Positif (FP) :</strong> fausse alerte — le modèle
              prédit une annulation qui n'a pas eu lieu. Risque de sur-vente ou
              d'action commerciale inutile. <strong>Faux Négatif (FN) :</strong>{" "}
              annulation manquée — le modèle n'a pas vu venir l'annulation.
              C'est l'erreur la plus coûteuse en RM.
            </Typography>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default MetricsGuide;
